import Typography from '@mui/material/Typography';
import React, { forwardRef, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronLeft, Search, X } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuCheckbox,
  PopupMenuInput,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { ID } from 'shared/types/id';

import * as Styled from './custom-form-multi-select.style';
import { NestedOptionItem } from './nested-option-item';
import { Option, Size } from './types';
import {
  filterOptionsTree,
  flattenOptions,
  getNextValuesAfterToggle,
} from './utils';

const defaultRenderOption: React.FC<{ value: Option[] }> = ({ value }) =>
  value.map(({ label }) => label).join(', ');

export type CustomFormMultiSelectProps = {
  id?: ID;
  options: Option[];
  value?: string[];
  placeholder?: string;
  interActionOption?: Option;
  onInteractionOption?: () => void;
  onChange?: (value?: string[]) => void;
  canClear?: boolean;
  renderValue?: React.FC<{ value: Option[] }>;
  noBorders?: boolean;
  size?: Size;
  inheritColor?: boolean;
  recursiveSelection?: boolean;
  noOverflow?: boolean;
  enableSearch?: boolean;
  optionsAsButtons?: boolean;
  mainOptions?: React.ReactNode;
  emptyLabel?: React.ReactNode;
  noOptionsLabel?: React.ReactNode;
};

export const CustomFormMultiSelect = forwardRef<
  HTMLDivElement,
  CustomFormMultiSelectProps
>(
  (
    {
      id,
      options,
      value,
      placeholder,
      onChange,
      interActionOption,
      onInteractionOption,
      canClear,
      renderValue: RenderValue = defaultRenderOption,
      noBorders,
      size = Size.Medium,
      inheritColor,
      recursiveSelection = false,
      noOverflow,
      enableSearch,
      optionsAsButtons,
      mainOptions,
      emptyLabel,
      noOptionsLabel,
    },
    ref,
  ) => {
    const containerRef = useForwardedRef<HTMLDivElement>(ref);
    const menuRef = useRef<HTMLDivElement>(null);
    const { menuOpen, openMenu, closeMenu } = useOpenMenu();
    const [searchValue, setSearchValue] = React.useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const { t } = useTranslation();

    React.useEffect(() => {
      if (menuOpen && enableSearch) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }, [menuOpen, enableSearch]);

    const filteredOptions = useMemo(() => {
      if (!enableSearch || !searchValue) {
        return options;
      }

      return filterOptionsTree(options, searchValue);
    }, [options, searchValue, enableSearch]);

    const optionsLists = useMemo(
      () =>
        filteredOptions.reduce<Option[][]>(
          (acc, option) => {
            acc[acc.length - 1].push(option);

            if (option.hasBottomSeparator) {
              acc.push([]);
            }

            return acc;
          },
          [[]],
        ),
      [filteredOptions],
    );

    const onClear = (e: React.MouseEvent) => {
      e.preventDefault();
      onChange?.(undefined);
    };

    const selectedOptions = useMemo(() => {
      if (!value?.length) {
        return [];
      }

      const flatOptions = flattenOptions(options);

      return flatOptions.filter((option) => value.includes(option.value));
    }, [options, value]);

    const handleOptionChange = useCallback(
      (option: Option) => {
        const nextValues = getNextValuesAfterToggle(
          option,
          value ?? [],
          recursiveSelection,
        );

        onChange?.(nextValues);
      },
      [onChange, recursiveSelection, value],
    );

    const onClose = () => {
      closeMenu();
      setSearchValue('');
    };

    const hasEmptyLabel = emptyLabel !== undefined && emptyLabel !== null;
    const showsActiveValue = !!selectedOptions.length || hasEmptyLabel;

    return (
      <Styled.InputContainer ref={containerRef}>
        <Styled.Button
          $noBorders={!!noBorders}
          $size={size}
          $inheritColor={!!inheritColor}
          $isPlaceholder={!showsActiveValue}
          $noOverflow={!!noOverflow}
          onClick={openMenu}
          type="button"
          id={id}
        >
          {selectedOptions.length ? (
            <RenderValue value={selectedOptions} />
          ) : hasEmptyLabel ? (
            emptyLabel
          ) : (
            placeholder
          )}
        </Styled.Button>

        <Styled.InteractionContainer
          $inheritColor={!!inheritColor}
          $size={size}
        >
          {showsActiveValue && canClear ? (
            <Styled.ClearButton onClick={onClear} type="button" tabIndex={-1}>
              <Icon icon={X} />
            </Styled.ClearButton>
          ) : (
            <Styled.IconContainer onClick={openMenu}>
              <Icon icon={ChevronLeft} />
            </Styled.IconContainer>
          )}
        </Styled.InteractionContainer>

        {menuOpen && (
          <PopupMenu
            ref={menuRef}
            referenceElement={containerRef}
            onOutside={onClose}
            position="bottom"
            fallbackPositions={[
              'top',
              'right-end',
              'right-start',
              'bottom-end',
              'bottom-start',
              'left-start',
              'left-end',
              'top-end',
              'top-start',
            ]}
            hasParentWidth
          >
            {enableSearch && (
              <PopupMenuList hasBottomBorder>
                <PopupMenuListItem>
                  <PopupMenuInput
                    ref={inputRef}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={t('general.labels.search.placeholder')}
                    startIcon={Search}
                  />
                </PopupMenuListItem>
              </PopupMenuList>
            )}
            {mainOptions}
            {optionsLists.map((optionsList, index) =>
              optionsList.length ? (
                <PopupMenuList
                  key={`options-${index}`}
                  hasBottomBorder={index !== optionsLists.length - 1}
                >
                  {optionsList.map((option) => {
                    const hasChildren = !!option.children?.length;
                    const checked = selectedOptions.some(
                      ({ value }) => option.value === value,
                    );

                    if (hasChildren) {
                      return (
                        <NestedOptionItem
                          key={option.value}
                          option={option}
                          selectedValues={value ?? []}
                          onChange={handleOptionChange}
                          level={0}
                          compact={size === Size.ExtraSmall}
                          forceExpanded={
                            enableSearch && !!searchValue ? true : undefined
                          }
                        />
                      );
                    }

                    const _onChange = () => handleOptionChange(option);

                    return (
                      <PopupMenuListItem key={option.value}>
                        {optionsAsButtons ? (
                          <PopupMenuButton
                            start={option.start}
                            end={
                              checked ? (
                                <Styled.CheckIconContainer>
                                  <Icon icon={Check} />
                                </Styled.CheckIconContainer>
                              ) : undefined
                            }
                            onClick={_onChange}
                          >
                            {option.label}
                          </PopupMenuButton>
                        ) : (
                          <PopupMenuCheckbox
                            label={option.label}
                            onChange={_onChange}
                            checked={checked}
                          />
                        )}
                      </PopupMenuListItem>
                    );
                  })}
                </PopupMenuList>
              ) : null,
            )}

            {!filteredOptions.length && !!noOptionsLabel && (
              <Styled.NoResultsContainer>
                <Typography variant="body2" color="text.secondary">
                  {noOptionsLabel}
                </Typography>
              </Styled.NoResultsContainer>
            )}

            {!filteredOptions.length &&
              enableSearch &&
              !!searchValue &&
              !interActionOption &&
              !noOptionsLabel && (
                <Styled.NoResultsContainer>
                  <Typography variant="body2" color="text.secondary">
                    No results
                  </Typography>
                </Styled.NoResultsContainer>
              )}

            {!!interActionOption && (
              <PopupMenuList>
                <PopupMenuListItem>
                  <PopupMenuButton
                    start={interActionOption.start}
                    end={
                      <Typography variant="overline">
                        {interActionOption.description}
                      </Typography>
                    }
                    onClick={onInteractionOption}
                  >
                    {interActionOption.label}
                  </PopupMenuButton>
                </PopupMenuListItem>
              </PopupMenuList>
            )}
          </PopupMenu>
        )}
      </Styled.InputContainer>
    );
  },
);
